import { format } from 'date-fns'
import { createPortal } from 'react-dom'

const MEAL_ICONS = { manual: '🍳', imported: '🌐', restaurant: '🥡' }

function MealCard({ meal, label, dayNotes, highlighted }) {
  if (!meal) return null
  return (
    <div className={`rounded-xl p-4 ${highlighted ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
      {label && <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">{label}</p>}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{MEAL_ICONS[meal.type] || '🍽️'}</span>
        <h3 className="font-semibold text-gray-900 text-lg">{meal.name}</h3>
      </div>

      {/* Manual meal */}
      {meal.type === 'manual' && (
        <>
          {meal.description && <p className="text-sm text-gray-600 mb-2">{meal.description}</p>}
          {meal.notes && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 font-medium">Notes</p>
              <p className="text-sm text-gray-700 mt-1">{meal.notes}</p>
            </div>
          )}
        </>
      )}

      {/* Imported recipe */}
      {meal.type === 'imported' && (
        <>
          {meal.summary && <p className="text-sm text-gray-600 mb-3">{meal.summary}</p>}
          <div className="flex gap-4 text-xs text-gray-500 mb-3">
            {meal.prepTime && <span>⏱️ {meal.prepTime}</span>}
            {meal.cookTime && <span>🔥 {meal.cookTime}</span>}
            {meal.servings && <span>👥 {meal.servings}</span>}
          </div>
          {meal.ingredients?.length > 0 && (
            <details className="mb-2">
              <summary className="text-sm font-medium text-gray-700 cursor-pointer">Ingredients ({meal.ingredients.length})</summary>
              <ul className="mt-2 space-y-1">
                {meal.ingredients.map((ing, i) => (
                  <li key={i} className="text-sm text-gray-600 flex gap-2"><span>•</span>{ing}</li>
                ))}
              </ul>
            </details>
          )}
          {meal.instructions?.length > 0 && (
            <details>
              <summary className="text-sm font-medium text-gray-700 cursor-pointer">Instructions ({meal.instructions.length} steps)</summary>
              <ol className="mt-2 space-y-2">
                {meal.instructions.map((step, i) => (
                  <li key={i} className="text-sm text-gray-600 flex gap-2">
                    <span className="font-medium text-gray-400 shrink-0">{i + 1}.</span>{step}
                  </li>
                ))}
              </ol>
            </details>
          )}
          {meal.sourceUrl && (
            <a href={meal.sourceUrl} target="_blank" rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-sm text-green-600 hover:text-green-700">
              View original recipe ↗
            </a>
          )}
        </>
      )}

      {/* Restaurant */}
      {meal.type === 'restaurant' && (
        <>
          {meal.address && <p className="text-sm text-gray-600 mb-2">📍 {meal.address}</p>}
          {meal.phoneNumber && <p className="text-sm text-gray-600 mb-2">📞 {meal.phoneNumber}</p>}
          {meal.mapsUrl && (
            <a href={meal.mapsUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-green-700 mb-2">
              Open in Google Maps ↗
            </a>
          )}
        </>
      )}

      {dayNotes && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 font-medium">Notes for this night</p>
          <p className="text-sm text-gray-700 mt-1">{dayNotes}</p>
        </div>
      )}
    </div>
  )
}

export default function MealDetailDrawer({ date, entry, meal, counterMeal, currentUser, onClose, onAgree, onWithdraw, onCounter }) {
  if (!entry) return null

  const isReadOnly = currentUser?.role === 'readonly'
  const { state } = entry

  const canAgree = !isReadOnly && (
    (state === 'proposed' && entry.proposal?.proposedBy !== currentUser?.userId) ||
    (state === 'countered' && entry.counter?.proposedBy !== currentUser?.userId)
  )
  const canWithdraw = !isReadOnly
  const canCounter = !isReadOnly && (state === 'proposed' && entry.proposal?.proposedBy !== currentUser?.userId)

  const activeMealForState = state === 'agreed'
    ? meal
    : state === 'proposed' ? meal
    : null // countered shows both

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-4 left-0 right-0 max-w-2xl mx-auto bg-white rounded-2xl z-50 max-h-[85vh] flex flex-col">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="px-4 pb-2 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">
              {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 pb-4 space-y-3">
          {/* Agreed state */}
          {state === 'agreed' && (
            <MealCard meal={meal} dayNotes={entry.agreedDayNotes} highlighted />
          )}

          {/* Proposed state */}
          {state === 'proposed' && (
            <MealCard meal={meal} label="Proposed dinner" dayNotes={entry.proposal?.dayNotes} />
          )}

          {/* Countered state - show both */}
          {state === 'countered' && (
            <>
              <MealCard meal={meal} label="Original proposal" dayNotes={entry.proposal?.dayNotes} />
              <MealCard meal={counterMeal} label="Counter-proposal" dayNotes={entry.counter?.dayNotes} highlighted />
            </>
          )}

          {/* Who proposed */}
          {state === 'proposed' && (
            <p className="text-xs text-gray-500 text-center">
              Proposed by {entry.proposal?.proposedBy === 'andrew' ? 'Andrew' : entry.proposal?.proposedBy === 'sara' ? 'Sara' : 'Devin'}
            </p>
          )}
          {state === 'agreed' && (
            <p className="text-xs text-green-600 text-center font-medium">✓ Both agreed</p>
          )}
        </div>

        {/* Actions */}
        {!isReadOnly && (
          <div className="p-4 border-t border-gray-100 flex flex-col gap-2">
            {canAgree && (
              <button onClick={onAgree} className="btn-primary w-full py-3 text-base">
                {state === 'countered' ? 'Accept counter-proposal ✓' : 'Agree to this ✓'}
              </button>
            )}
            {canCounter && (
              <button onClick={onCounter} className="btn-secondary w-full py-3">
                Propose something else
              </button>
            )}
            <button onClick={onWithdraw} className="text-sm text-red-500 hover:text-red-600 text-center py-1">
              Remove selection
            </button>
          </div>
        )}
      </div>
    </>,
  document.body
  )
}
